import React, { useState, useCallback, useEffect } from "react";
import { Stack, Text, Center, Paper } from "@mantine/core";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
  LoadingIndicator,
} from "@/components/generic";
import {
  LookupDictionaryItemsTable,
  LookupDictionaryItemEditModal,
  LookupDictionarySelector,
} from "@/components/features";
import { useLookupDictionaries, useLookupDictionaryItems } from "@/hooks";
import {
  LookupDictionaryItem,
  LookupDictionaryItemInput,
  LookupDictionary,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE, OPERATION_RIGHTS } from "@/config";
import { STATUSES } from "@sdflc/utils";

export const LookupDictionaryItemsPage: React.FC<PageProps> = (
  props: PageProps
) => {
  const { user } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string; itemId?: string }>();

  // Get dictionary ID from URL
  const dictionaryIdFromUrl = params.id;
  const itemIdFromUrl = params.itemId;

  // Parse URL to determine which modal to show
  const isAddMode = location.pathname.endsWith("/add");
  const isEditMode = !!itemIdFromUrl && location.pathname.endsWith("/edit");

  // Use the lookup dictionaries hook for dictionary selector
  const {
    lookupDictionaries,
    loading: dictionariesLoading,
    isEmpty: noDictionaries,
  } = useLookupDictionaries({
    autoLoad: true,
    userId: user?.id,
    initialFilters: { status: [STATUSES.ACTIVE] },
  });

  // Use the lookup dictionary items hook for items management
  const {
    lookupDictionaryItems,
    loading: itemsLoading,
    loadingMore,
    hasMore,
    isEmpty: noItems,
    filters,
    sortBy,
    isCreating,
    isUpdating,
    removingId,
    list: listItems,
    loadMore,
    refresh,
    get: getItem,
    create,
    update,
    remove,
    lastError,
    clearError,
  } = useLookupDictionaryItems({
    autoLoad: false,
    userId: user?.id,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Local UI state
  const [itemToDelete, setItemToDelete] = useState<LookupDictionaryItem | null>(
    null
  );
  const [searchValue, setSearchValue] = useState<string>("");
  const [editingItem, setEditingItem] = useState<LookupDictionaryItem | null>(
    null
  );
  const [editedItemId, setEditedItemId] = useState<string | null>(null);
  const [selectedDictionary, setSelectedDictionary] =
    useState<LookupDictionary | null>(null);

  // Find current dictionary from loaded dictionaries
  useEffect(() => {
    if (dictionaryIdFromUrl && lookupDictionaries.length > 0) {
      const dictionary = lookupDictionaries.find(
        (d) => d.id === dictionaryIdFromUrl
      );
      setSelectedDictionary(dictionary || null);
    }
  }, [dictionaryIdFromUrl, lookupDictionaries]);

  // Load items when dictionary ID changes
  useEffect(() => {
    if (dictionaryIdFromUrl) {
      const itemFilters = {
        lookupDictionaryId: [dictionaryIdFromUrl],
        status: [STATUSES.ACTIVE],
      };
      listItems(
        itemFilters,
        { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        sortBy,
        true
      );
    }
  }, [dictionaryIdFromUrl, listItems, sortBy]);

  // Load item when in edit mode
  useEffect(() => {
    if (isEditMode && itemIdFromUrl) {
      const loadItem = async () => {
        await getItem(itemIdFromUrl);
      };
      loadItem();
    }
  }, [isEditMode, itemIdFromUrl, getItem]);

  // Update editing item when we get data
  useEffect(() => {
    if (itemIdFromUrl) {
      const item = lookupDictionaryItems.find((i) => i.id === itemIdFromUrl);
      if (item) {
        if (isEditMode) {
          setEditingItem(item);
        }
      }
    } else {
      setEditingItem(null);
    }
  }, [isEditMode, itemIdFromUrl, lookupDictionaryItems]);

  // Handle dictionary change from selector
  const handleDictionaryChange = useCallback(
    (dictionaryId: string | null, dictionary?: LookupDictionary) => {
      if (dictionaryId) {
        setSelectedDictionary(dictionary || null);
        navigate(`/lookup-dictionaries/${dictionaryId}/items`);
      }
    },
    [navigate]
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      if (!dictionaryIdFromUrl) return;

      setSearchValue(value);

      const newFilters: any = {
        ...filters,
        lookupDictionaryId: [dictionaryIdFromUrl],
      };

      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      } else {
        delete newFilters.searchKeyword;
      }

      listItems(
        newFilters,
        { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        sortBy,
        true
      );
    },
    [dictionaryIdFromUrl, filters, sortBy, listItems]
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (newSortBy: any[]) => {
      if (!dictionaryIdFromUrl) return;

      const currentFilters = {
        ...filters,
        lookupDictionaryId: [dictionaryIdFromUrl],
      };

      listItems(
        currentFilters,
        { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        newSortBy,
        true
      );
    },
    [dictionaryIdFromUrl, filters, listItems]
  );

  // Handle add button click
  const handleAddItem = useCallback(() => {
    if (!dictionaryIdFromUrl) return;
    clearError();
    navigate(`/lookup-dictionaries/${dictionaryIdFromUrl}/items/add`);
  }, [dictionaryIdFromUrl, navigate, clearError]);

  // Handle edit item
  const handleEditItem = useCallback(
    (item: LookupDictionaryItem) => {
      if (!dictionaryIdFromUrl) return;
      clearError();
      if (item.id) {
        navigate(
          `/lookup-dictionaries/${dictionaryIdFromUrl}/items/${item.id}/edit`
        );
      }
    },
    [dictionaryIdFromUrl, navigate, clearError]
  );

  // Handle remove item button click
  const handleRemoveItemClick = useCallback((item: LookupDictionaryItem) => {
    setItemToDelete(item);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (itemToDelete?.id) {
      const success = await remove(itemToDelete.id);
      if (success) {
        setItemToDelete(null);
        // If we're currently editing this item, close the modal
        if (itemIdFromUrl === itemToDelete.id) {
          navigate(`/lookup-dictionaries/${dictionaryIdFromUrl}/items`);
        }
      }
    }
  }, [itemToDelete, remove, itemIdFromUrl, dictionaryIdFromUrl, navigate]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setItemToDelete(null);
  }, []);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setEditingItem(null);
    clearError();
    navigate(`/lookup-dictionaries/${dictionaryIdFromUrl}/items`);
  }, [dictionaryIdFromUrl, navigate, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle back to dictionaries
  const handleBackToDictionaries = useCallback(() => {
    navigate("/lookup-dictionaries");
  }, [navigate]);

  // Handle item creation
  const handleCreateItem = useCallback(
    async (
      data: LookupDictionaryItemInput
    ): Promise<LookupDictionaryItem | null> => {
      const result = await create(data);
      if (result?.id) {
        setEditedItemId(result.id);
        navigate(`/lookup-dictionaries/${dictionaryIdFromUrl}/items`);
      }
      return result;
    },
    [create, dictionaryIdFromUrl, navigate]
  );

  // Handle item update
  const handleUpdateItem = useCallback(
    async (
      id: string,
      data: LookupDictionaryItemInput
    ): Promise<LookupDictionaryItem | null> => {
      const result = await update(id, data);
      if (result) {
        setEditedItemId(result.id || null);
        navigate(`/lookup-dictionaries/${dictionaryIdFromUrl}/items`);
      }
      return result;
    },
    [update, dictionaryIdFromUrl, navigate]
  );

  // Render loading state while dictionaries are loading
  if (dictionariesLoading && lookupDictionaries.length === 0) {
    return (
      <DashboardLayout {...props}>
        <Center h={300}>
          <Stack align="center" gap="md">
            <LoadingIndicator size="lg" />
            <Text c="dimmed">Loading dictionaries...</Text>
          </Stack>
        </Center>
      </DashboardLayout>
    );
  }

  // Render empty state when no dictionaries exist
  if (noDictionaries && !dictionariesLoading) {
    return (
      <DashboardLayout {...props}>
        <Center h={300}>
          <Paper p="xl" withBorder>
            <Stack align="center" gap="md">
              <Text size="lg" fw={500}>
                No Lookup Dictionaries
              </Text>
              <Text c="dimmed" ta="center" maw={400}>
                You need to create a lookup dictionary before you can add items.
                Dictionaries define the validation rules for your form fields.
              </Text>
              <AppButton iconName="Plus" onClick={handleBackToDictionaries}>
                Go to Dictionaries
              </AppButton>
            </Stack>
          </Paper>
        </Center>
      </DashboardLayout>
    );
  }

  const canEdit = selectedDictionary?.rights?.includes(OPERATION_RIGHTS.UPDATE);

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with dictionary selector, actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "back",
              items: [
                <AppButton
                  key="back"
                  variant="secondary"
                  iconName="ArrowLeft"
                  onClick={handleBackToDictionaries}
                />,
                <LookupDictionarySelector
                  key="dictionary-selector"
                  value={dictionaryIdFromUrl}
                  onChange={handleDictionaryChange}
                  required
                  placeholder="Select dictionary"
                  style={{ minWidth: 250 }}
                />,
                <AppButton
                  key="add-item"
                  iconName="Plus"
                  onClick={handleAddItem}
                  disabled={
                    !dictionaryIdFromUrl ||
                    isCreating ||
                    !!removingId ||
                    itemsLoading ||
                    !canEdit
                  }
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={
                    !dictionaryIdFromUrl ||
                    isCreating ||
                    !!removingId ||
                    itemsLoading
                  }
                />,
              ],
              align: "left",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search items..."
                  value={searchValue}
                  onChange={(value) => handleSearchChange(value)}
                  disabled={!dictionaryIdFromUrl}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Dictionary info header */}
        {selectedDictionary && (
          <Paper p="sm" withBorder>
            <Text size="sm">
              <Text span fw={500}>
                {selectedDictionary.name}
              </Text>
              {selectedDictionary.description && (
                <Text span c="dimmed">
                  {" "}
                  — {selectedDictionary.description}
                </Text>
              )}
            </Text>
          </Paper>
        )}

        {/* Dictionary items table */}
        {dictionaryIdFromUrl && (
          <LookupDictionaryItemsTable
            lookupDictionaryItems={lookupDictionaryItems}
            loading={itemsLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onLoadMore={loadMore}
            onEditItem={handleEditItem}
            onRemoveItem={handleRemoveItemClick}
            removingItemId={removingId}
            editedItemId={editedItemId}
            lookupDictionary={selectedDictionary}
          />
        )}

        {/* Empty state when dictionary has no items */}
        <EmptyState
          display={noItems && !itemsLoading && !!dictionaryIdFromUrl}
          title="No items in this dictionary"
          description="Add items to this lookup dictionary to use for form field validation"
          actionIcon="Plus"
          actionLabel="Add Item"
          onAction={handleAddItem}
        />
      </Stack>

      {/* Item edit modal for create/edit */}
      {dictionaryIdFromUrl && (
        <LookupDictionaryItemEditModal
          opened={isAddMode || isEditMode}
          onClose={handleEditModalClose}
          lookupDictionaryId={dictionaryIdFromUrl}
          lookupDictionaryItem={isEditMode ? editingItem : null}
          onCreateItem={handleCreateItem}
          onUpdateItem={handleUpdateItem}
          isCreating={isCreating}
          isUpdating={isUpdating}
          lastError={lastError}
          onClearError={clearError}
        />
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!itemToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Dictionary Item"
        message={`Are you sure you want to delete "${itemToDelete?.checkValue}"?`}
        details="This action cannot be undone."
        confirmInProgress={removingId === itemToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
